import React from 'react';
import {
  Box,
  Paper,
  TableContainer,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  Collapse,
  styled,
  tableCellClasses,
  TableCell
} from '@mui/material';
import {
  ArrowDropDown as ArrowDropDownIcon,
  ArrowDropUp as ArrowDropUpIcon,
  Code as CodeIcon,
  InfoOutlined as InfoOutlinedIcon,
  FilterAlt as FilterAltIcon
} from '@mui/icons-material';
import { rgba } from '../../../utils/colors';
import Flex from '../../../components/base/Flex';
import { StyledTableRow2 } from '../../../components/base/Table';

const StatusBadge = styled(Box)(({ theme, status }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(0.5, 1),
  borderRadius: 4,
  fontWeight: 600,
  fontSize: '0.75rem',
  minWidth: 60,
  backgroundColor: getStatusColor(status, theme),
  color: theme.palette.getContrastText(getStatusColor(status, theme))
}));

const MethodChip = styled(Chip)(({ theme, method }) => ({
  fontWeight: 500,
  textTransform: 'uppercase',
  backgroundColor: getMethodColor(method, theme),
  color: theme.palette.getContrastText(getMethodColor(method, theme)),
  minWidth: 70
}));

const JsonPreview = styled(Box)(({ theme }) => ({
  fontFamily: 'monospace',
  fontSize: '0.8rem',
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  margin: theme.spacing(1, 0),
  overflowX: 'auto',
  whiteSpace: 'pre-wrap',
  border: `1px solid ${theme.palette.divider}`
}));

// Helper functions
const getStatusColor = (status, theme) => {
  const code = Math.floor(status / 100);
  switch (code) {
    case 2:
      return theme.palette.success.light;
    case 3:
      return theme.palette.info.light;
    case 4:
      return theme.palette.warning.light;
    case 5:
      return theme.palette.error.light;
    default:
      return theme.palette.grey[300];
  }
};

const getMethodColor = (method, theme) => {
  switch (method?.toUpperCase()) {
    case 'GET':
      return theme.palette.info.main;
    case 'POST':
      return theme.palette.success.main;
    case 'PUT':
      return theme.palette.warning.main;
    case 'DELETE':
      return theme.palette.error.main;
    case 'PATCH':
      return theme.palette.secondary.main;
    default:
      return theme.palette.grey[500];
  }
};

const formatSize = (bytes) => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatTime = (ms) => {
  if (!ms) return '-';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
};

const NetworkRow = React.memo(({ index, isOpen, onRowClick, log }) => {
  const theme = useTheme();

  const getContentType = () => {
    return (
      log?.response?.headers
        ?.find((h) => h.name.toLowerCase() === 'content-type')
        ?.value?.split(';')[0] || '-'
    );
  };

  const getContentSize = () => {
    const size = log?.response?.headers?.find(
      (h) => h.name.toLowerCase() === 'content-length'
    )?.value;
    return size ? formatSize(Number(size)) : '-';
  };

  return (
    <React.Fragment>
      <StyledTableRow2
        onClick={() => onRowClick(index)}
        sx={{
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
            cursor: 'pointer'
          }
        }}>
        <StyledTableCell2>
          <Flex alignItems="center" gap={1}>
            <MethodChip
              size="small"
              label={log.request?.method || '-'}
              method={log.request?.method}
            />
            {isOpen ? <ArrowDropUpIcon color="action" /> : <ArrowDropDownIcon color="action" />}
          </Flex>
        </StyledTableCell2>

        <StyledTableCell2>
          <StatusBadge status={log?.response?.status}>{log?.response?.status || '-'}</StatusBadge>
        </StyledTableCell2>

        <StyledTableCell2 sx={{ maxWidth: 300 }}>
          <Tooltip title={log?.request?.url || ''} placement="top-start">
            <Typography variant="body2" noWrap>
              {log?.request?.url || '-'}
            </Typography>
          </Tooltip>
        </StyledTableCell2>

        <StyledTableCell2>
          <Typography variant="body2">{getContentType()}</Typography>
        </StyledTableCell2>

        <StyledTableCell2>
          <Typography variant="body2">{getContentSize()}</Typography>
        </StyledTableCell2>

        <StyledTableCell2 align="right">
          <Typography variant="body2">{formatTime(log.time)}</Typography>
        </StyledTableCell2>
      </StyledTableRow2>

      <TableRow>
        <StyledTableCell2 colSpan={6} sx={{ p: 0, border: 0 }}>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <Box p={2}>
              <Flex alignItems="center" gap={1} mb={1}>
                <CodeIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" color="text.secondary">
                  Request/Response Details
                </Typography>
              </Flex>
              <JsonPreview>{JSON.stringify(log, null, 2)}</JsonPreview>
            </Box>
          </Collapse>
        </StyledTableCell2>
      </TableRow>
    </React.Fragment>
  );
});

const NetworkLogsTable = ({ openRow, setOpenRow, networkLogs }) => {
  const theme = useTheme();
  const [filter, setFilter] = React.useState('all');

  const handleRowClick = (index) => {
    setOpenRow((prev) => (prev === index ? null : index));
  };

  const filteredLogs = networkLogs?.filter((log) => {
    if (filter === 'all') return true;
    const status = log?.response?.status;
    if (!status) return false;
    const code = Math.floor(status / 100);
    return filter === 'success'
      ? code === 2
      : filter === 'redirect'
      ? code === 3
      : filter === 'clientError'
      ? code === 4
      : filter === 'serverError'
      ? code === 5
      : true;
  });

  return (
    <Paper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        overflow: 'hidden'
      }}
      elevation={0}>
      <Box
        sx={{
          p: 2,
          bgcolor: 'background.paper',
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
        <Typography variant="h6" fontWeight={500}>
          Network Requests
        </Typography>

        <Flex alignItems="center" gap={1}>
          <Chip label={`${networkLogs?.length || 0} total`} size="small" variant="outlined" />
        </Flex>
      </Box>

      <TableContainer sx={{ flex: 1, maxHeight: '80vh' }}>
        <Table stickyHeader size="small" sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              <StyledTableCell>
                <Flex alignItems="center" gap={0.5}>
                  Method
                  <Tooltip title="HTTP request method">
                    <InfoOutlinedIcon fontSize="small" color="action" />
                  </Tooltip>
                </Flex>
              </StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>URL</StyledTableCell>
              <StyledTableCell>Type</StyledTableCell>
              <StyledTableCell>Size</StyledTableCell>
              <StyledTableCell align="right">Time</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLogs?.length ? (
              filteredLogs.map((log, i) => (
                <NetworkRow
                  key={i}
                  index={i}
                  log={log}
                  isOpen={openRow === i}
                  onRowClick={handleRowClick}
                />
              ))
            ) : (
              <TableRow>
                <StyledTableCell2 colSpan={6}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 200,
                      color: 'text.secondary'
                    }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        bgcolor: theme.palette.action.hover,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2
                      }}>
                      <Typography variant="h4" color="text.secondary">
                        📋
                      </Typography>
                    </Box>
                    <Typography variant="body1" gutterBottom>
                      No network requests recorded
                    </Typography>
                    <Typography variant="caption">Network activity will appear here</Typography>
                  </Box>
                </StyledTableCell2>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    fontWeight: 500,
    borderBottom: `2px solid ${theme.palette.divider}`,
    '&:not(:first-of-type)': {
      maxWidth: '200px'
    }
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14
  }
}));

const StyledTableCell2 = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: 'transparent',
    color: 'inherit',
    borderBottom: `2px solid ${theme.palette.divider}`,
    '&:not(:first-of-type)': {
      maxWidth: '200px'
    }
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: '8px 16px',
    '&:not(:first-of-type)': {
      maxWidth: '200px'
    }
  }
}));

export default NetworkLogsTable;
